import {
  RefObject, useCallback, useEffect, useState,
} from 'react';

interface Dimensions {
  width: number;
  height: number;
}

const getDimensions = (element: HTMLElement, includePadding: boolean): Dimensions => {
  const width = element.offsetWidth;
  const height = element.offsetHeight;

  if (includePadding) {
    return { width, height };
  }

  const styles = window.getComputedStyle(element);

  return {
    width: width - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight),
    height: height - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom),
  };
};

const useElementSize = <T extends HTMLElement>(
  ref: RefObject<T>,
  includePadding: boolean = true,
): Dimensions => {
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 0,
    height: 0,
  });

  const handleResize = useCallback(() => {
    if (ref.current) {
      setDimensions(getDimensions(ref.current, includePadding));
    }
  }, [ref.current, includePadding]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);

    if (ref.current) {
      handleResize();

      resizeObserver.observe(ref.current);
    }

    return () => resizeObserver.disconnect();
  }, [ref.current, handleResize]);

  return dimensions;
};

export default useElementSize;
